package scenes.levels

import AppIntegrations
import assets.*
import b2d.*
import com.github.quillraven.fleks.*
import com.soywiz.klock.*
import com.soywiz.korge.input.*
import com.soywiz.korge.scene.*
import com.soywiz.korge.ui.*
import com.soywiz.korge.view.*
import com.soywiz.korge.view.Camera
import com.soywiz.korge.view.camera.*
import com.soywiz.korim.bitmap.*
import com.soywiz.korim.color.*
import com.soywiz.korim.font.*
import com.soywiz.korim.format.*
import com.soywiz.korim.text.*
import com.soywiz.korio.file.std.*
import component.*
import game.*
import i18n.*
import input.*
import org.jbox2d.common.*
import scenes.*
import scenes.test.*
import system.*
import system.single.*
import ui.*
import ui.hud.*
import utils.*

abstract class BaseLevel(
    private val levelFile: String,
    private val levelWidth: Double,
    private val levelHeight: Double
) : Scene() {
    private val animations = mutableMapOf<String, SpriteAnimation>()

    private val physicWorld: org.jbox2d.dynamics.World = org.jbox2d.dynamics.World(Vec2(0f, 10f)).apply {
        customScale = GameDimensions.PPM.toDouble()
        setContactListener(WorldContactListener())
    }
    private lateinit var world: World

    val camera = Camera()

    private val bmpMap = mutableMapOf<String, Bitmap>()
    private val fonts = mutableMapOf<String, Font>()

    private val isDesktop = AppIntegrations.yaSdk?.deviceInfo?.isDesktop() ?: true

    private val sessionData = SessionData(isPaused = false)

    abstract suspend fun changeToNextLevel()

    override suspend fun SContainer.sceneInit() {
        println("SinglePlayerScene sceneInit")

        // load assets
        loadPlayerAnimation("characters/ninja_frog").forEach {
            animations["ninja-frog-${it.key.name}"] = it.value
        }

        animations["coins"] = loadCoinsAnimation()
        animations["diamonds-1"] = loadDiamonds1Animation()
        animations["diamonds-2"] = loadDiamonds2Animation()
        animations["diamonds-3"] = loadDiamonds3Animation()
        bmpMap["coin-icon"] = resourcesVfs["cash/coin-icon.png"].readBitmap()

        fonts[FontsLoader.RUSSO_ONE_REGULAR] = FontsLoader.loadFont(FontsLoader.RUSSO_ONE_REGULAR)
        fonts[FontsLoader.GOGONO_COCOA_MOCHI_CYRILLIC] = FontsLoader.loadFont(FontsLoader.GOGONO_COCOA_MOCHI_CYRILLIC)

        if (!isDesktop) {
            bmpMap["back"] = resourcesVfs["icons/move_buttons/back.png"].readBitmap()
            bmpMap["back_active"] = resourcesVfs["icons/move_buttons/back_active.png"].readBitmap()
            bmpMap["forward"] = resourcesVfs["icons/move_buttons/forward.png"].readBitmap()
            bmpMap["forward_active"] = resourcesVfs["icons/move_buttons/forward_active.png"].readBitmap()
            bmpMap["jump"] = resourcesVfs["icons/move_buttons/jump.png"].readBitmap()
            bmpMap["jump_active"] = resourcesVfs["icons/move_buttons/jump_active.png"].readBitmap()
        }

        bmpMap["dash"] = resourcesVfs["icons/move_buttons/dash.png"].readBitmap()
        bmpMap["dash_active"] = resourcesVfs["icons/move_buttons/dash_active.png"].readBitmap()
        bmpMap["dash_disabled"] = resourcesVfs["icons/move_buttons/dash_disabled.png"].readBitmap()

        bmpMap["button_icon_green"] = resourcesVfs["lobby_buttons/lobby_green.png"].readBitmap()
        bmpMap["button_icon_active"] = resourcesVfs["lobby_buttons/lobby_pressed.png"].readBitmap()
        bmpMap["button_icon_purple"] = resourcesVfs["lobby_buttons/lobby_purple.png"].readBitmap()

        bmpMap["button_menu"] = resourcesVfs["icons/menu_button_v2.png"].readBitmap()
        bmpMap["button_menu_active"] = resourcesVfs["icons/menu_button_v2_active.png"].readBitmap()

        val bgLayers = loadBackground()
        val cameraContainer =
            cameraContainer(GameDimensions.V_WIDTH.toDouble(), GameDimensions.V_HEIGHT.toDouble(), clip = true) {
                camera.addTo(this)
            }

        val worldLayers = WorldLayers(Container(), Container(), Container(), Container(), Container())
        worldLayers.cave.addTo(camera)
        worldLayers.items.addTo(camera)
        worldLayers.ground.addTo(camera)
        worldLayers.traps.addTo(camera)
        worldLayers.water.addTo(camera)

        val worldMetaData = WorldObjectFactory.loadLevel(physicWorld, worldLayers, levelFile)

        val controllerData = controllerData(cameraContainer)
        val menuButton = cameraContainer.menuButton()
        val gameHUD: GameHUD = if (isDesktop) {
            DesktopHUD(
                menuButton = menuButton,
                dashButton = controllerData.dashButton!!,
            )
        } else {
            TouchHUD(
                leftArrow = controllerData.leftArrow!!,
                rightArrow = controllerData.rightArrow!!,
                jumpButton = controllerData.jumpButton!!,
                dashButton = controllerData.dashButton!!,
                menuButton = menuButton,
            )
        }

        val menuView = menuView()
        menuButton.apply {
            onUp {
                sessionData.isPaused = true
                menuView.visible = true
            }
        }

        val coinCounterTextView = UIText("0", width = 64.0, height = 64.0)
            .position(64.0, 0.0)
            .apply {
                textFont = fonts[FontsLoader.GOGONO_COCOA_MOCHI_CYRILLIC] ?: DefaultUIFont
                textAlignment = TextAlignment.MIDDLE_LEFT
            }

        cameraContainer.container {
            val bmp = bmpMap["coin-icon"] ?: Bitmaps.transparent.bmp
            image(bmp)

            coinCounterTextView.addTo(this)
        }

        world = world {
            injectables {
                add(SCENE_CONTAINER, this@sceneInit)
                add(physicWorld)
                add(cameraContainer)
                add(camera)
                add(controllerData.controller)
                add(worldMetaData)
                add(controllerData)
                add(worldLayers)
                add(bgLayers)
                add(gameHUD)
                add(sessionData)
            }

            components {
                onAdd(PhysicComponent, PhysicComponent.onAddPhysic)
                onRemove(PhysicComponent, PhysicComponent.onRemovePhysic)
            }

            val startCountDownTextView = UIText(
                text = "",
                width = cameraContainer.width,
                height = 36.0
            ).addTo(cameraContainer)
                .position(0.0, cameraContainer.height / 2.0 - 18.0)
                .apply {
                    textSize = 48.0
                    textAlignment = TextAlignment.MIDDLE_CENTER
                    textFont = fonts[FontsLoader.RUSSO_ONE_REGULAR] ?: DefaultUIFont
                }

            systems {
                add(PauseSystem())
                add(StartCountdownSystem(startCountDownTextView).apply { enabled = false })
//                add(YaSdkDebugSystem())

                add(PlayerSpawnSystem().apply { respawnPlayer = true })
                add(PlayerControlSystem().apply { enabled = true })

                add(DashControlSystem())
                add(AbilityControlSystem())
                add(AbilityApplySystem())

                add(PlayerTrapSystem())

                add(PreBox2dPhysicSystem())
                add(Box2dStepSystem())
                add(PhysicCorrectionsSystem())
//                add(PhysicDebugSystem().apply { enabled = true })

                add(PlayerStateSystem())

                add(ParallaxSystem())
                add(PlayerAnimationSystem())
                add(SpriteAnimationSystem(animations))
                add(FinishSystem(onPlayerFinished = { coinsCount ->
                    showModal(this@sceneInit, coinsCount)
                    AppIntegrations.yaSdk?.adv?.showFullscreenAdv()
                }))

                add(TrapAnimationSystem())

//                add(LevelAreaLimiterSystem(xLimitLeft, xLimitRight, yLimitTop, yLimitBottom))

//                add(AbilitySelectionSystem(selectFun = selectAbilitiesDialogFun(cameraContainer)))
                add(AbilityCooldownSystem())
                add(PlayerCoinCollectionSystem(coinCounterTextView))
                add(CoinCollectionSystem())
                add(PlayerCameraSystem(levelWidth, levelHeight))
            }
        }

        worldMetaData.traps.forEach { trap ->
            world.entity {
                it += trap
            }
        }

        worldMetaData.coins.forEach { coin ->
            world.entity {
                it += coin

                it += SpriteAnimationComponent(
                    spriteDisplayTime = if (coin.cashType == CashTypes.COIN) 150.milliseconds else 200.milliseconds
                ).apply {
                    nextAnimation = when (coin.cashType) {
                        CashTypes.COIN -> "coins"
                        CashTypes.DIAMOND_1 -> "diamonds-1"
                        CashTypes.DIAMOND_2 -> "diamonds-2"
                        CashTypes.DIAMOND_3 -> "diamonds-3"
                    }
                    this.sprite = coin.view
                }
            }
        }
    }

    override suspend fun SContainer.sceneMain() {
        println("SinglePlayerScene sceneMain")

        val fps = 60
        val dt = 1f / fps

        addFixedUpdater(fps.timesPerSecond) {
            world.update(dt)
        }
    }

    private fun Container.controllerData(cameraContainer: CameraContainer): InputControllerData {
        return if (isDesktop) {
            addKeyboardController(
                width = cameraContainer.width,
                height = cameraContainer.height,
                dashIcon = getBitmapSlice("dash"),
                dashActiveIcon = getBitmapSlice("dash_active"),
                dashDisabledIcon = getBitmapSlice("dash_disabled")
            )
        } else {
            addScreenControllerArrows(
                width = cameraContainer.width,
                height = cameraContainer.height,
                leftArrowIcon = getBitmapSlice("back"),
                leftArrowActiveIcon = getBitmapSlice("back_active"),
                rightArrowIcon = getBitmapSlice("forward"),
                rightArrowActiveIcon = getBitmapSlice("forward_active"),
                jumpIcon = getBitmapSlice("jump"),
                jumpActiveIcon = getBitmapSlice("jump_active"),
                dashIcon = getBitmapSlice("dash"),
                dashActiveIcon = getBitmapSlice("dash_active"),
                dashDisabledIcon = getBitmapSlice("dash_disabled")
            )
        }
    }

    private fun getBitmapSlice(id: String): BitmapSlice<*> {
        return bmpMap[id]?.slice() ?: Bitmaps.white
    }

    private suspend fun Container.loadBackground(): ParallaxLayers {
        val dir = "background/forest"
        val bgBitmap = resourcesVfs["$dir/01.png"].readBitmap()
        val clouds1 = resourcesVfs["$dir/02.png"].readBitmap()
        val clouds2 = resourcesVfs["$dir/03.png"].readBitmap()
        val clouds3 = resourcesVfs["$dir/04.png"].readBitmap()

        return managedParallax(bgBitmap, clouds1, clouds2, clouds3)
    }

    private fun showModal(container: Container, counsCount: Int) {
        container.container {
            solidRect(
                width = GameDimensions.V_WIDTH,
                height = GameDimensions.V_HEIGHT,
                color = Colors.BLACK
            ).alpha(0.5)

            uiText(
                text = Localization.strings.scene.baseLevel.victory,
                width = GameDimensions.V_WIDTH.toDouble(),
                height = 64.0
            )
                .position(0.0, GameDimensions.V_HEIGHT / 4.0 - 24.0)
                .apply {
                    textSize = 32.0

                    textColor = Colors.WHITE

                    textAlignment = TextAlignment.MIDDLE_CENTER
                    textFont = fonts[FontsLoader.RUSSO_ONE_REGULAR] ?: DefaultUIFont
                }

            val bmp = bmpMap["coin-icon"] ?: Bitmaps.transparent.bmp
            image(bmp)
                .position(GameDimensions.V_WIDTH / 2.0 - 64.0, GameDimensions.V_HEIGHT / 16.0 * 5.0)

            uiText("X   $counsCount", width = 64.0, height = 64.0)
                .position(GameDimensions.V_WIDTH / 2.0, GameDimensions.V_HEIGHT / 16.0 * 5.0)
                .apply {
                    textSize = 16.0

                    textColor = Colors.WHITE

                    textAlignment = TextAlignment.MIDDLE_CENTER
                    textFont = fonts[FontsLoader.RUSSO_ONE_REGULAR] ?: DefaultUIFont
                }

            victoryContinueButton()
        }
    }

    private fun Container.victoryContinueButton(): CustomButton {
        val width = 258.6
        val height = 82.5

        val button = CustomButton(
            label = Localization.strings.scene.baseLevel.menuContinue,
            width = width,
            height = height,
            icon = bmpMap["button_icon_green"]?.slice(),
            activeIcon = bmpMap["button_icon_active"]?.slice(),
            buttonId = -1
        )

        button.container.addTo(this)
        button.container.position(
            GameDimensions.V_WIDTH / 2.0 - width / 2.0,
            GameDimensions.V_HEIGHT / 8.0 * 5.0
        )
        button.text.apply {
            textSize = 36.0
            textFont = fonts[FontsLoader.GOGONO_COCOA_MOCHI_CYRILLIC] ?: DefaultUIFont
        }

        button.container.onClick {
            changeToNextLevel()
        }

        return button
    }

    fun Container.menuButton(): CustomButton {
        val button = CustomButton(
            label = "",
//            label = Localization.strings.scene.baseLevel.menu,
            width = 48.0,
            height = 48.0,
            icon = bmpMap["button_menu"]?.slice(),
            activeIcon = bmpMap["button_menu_active"]?.slice(),
            buttonId = -1
        ).apply {
            text.textSize = 32.0
            text.textColor = Colors.WHITE
            text.textFont = fonts[FontsLoader.GOGONO_COCOA_MOCHI_CYRILLIC] ?: DefaultUIFont

            container.position(this@menuButton.width - this.container.width - 20.0, 20.0)
        }

        button.container.addTo(this)
        return button
    }

    fun Container.menuView(): FixedSizeContainer {
        return fixedSizeContainer(this.width, this.height) {
            val view = this
            val width = this.width
            val height = this.height

            solidRect(
                width = width,
                height = height,
                color = Colors.BLACK
            ).alpha(0.5)

            menuResumeButton(
                menuDialog = view
            )

            menuExitButton()

        }.apply {
            visible = false
        }
    }

    private fun Container.menuResumeButton(
        menuDialog: View
    ): CustomButton {
        val width = 258.6
        val height = 82.5
        val marginBottom = 54.0

        val button = CustomButton(
            label = Localization.strings.scene.baseLevel.menuResume,
            width = width,
            height = height,
            icon = bmpMap["button_icon_green"]?.slice(),
            activeIcon = bmpMap["button_icon_active"]?.slice(),
            buttonId = -1
        )

        button.container.addTo(this)
        button.container.position(
            GameDimensions.V_WIDTH / 2.0 - width / 2.0,
            GameDimensions.V_HEIGHT - 64.0 - height * 2 - marginBottom
        )
        button.text.apply {
            textSize = 36.0
            textFont = fonts[FontsLoader.GOGONO_COCOA_MOCHI_CYRILLIC] ?: DefaultUIFont
        }

        button.container.onClick {
            menuDialog.visible = false
            sessionData.isPaused = false
        }

        return button
    }

    private fun Container.menuExitButton(): CustomButton {
        val width = 258.6
        val height = 82.5
        val marginBottom = 64.0

        val button = CustomButton(
            label = Localization.strings.scene.baseLevel.menuExit,
            width = width,
            height = height,
            icon = bmpMap["button_icon_purple"]?.slice(),
            activeIcon = bmpMap["button_icon_active"]?.slice(),
            buttonId = -1
        )

        button.container.addTo(this)
        button.container.position(
            GameDimensions.V_WIDTH / 2.0 - width / 2.0,
            GameDimensions.V_HEIGHT - height - marginBottom
        )
        button.text.apply {
            textSize = 36.0
            textFont = fonts[FontsLoader.GOGONO_COCOA_MOCHI_CYRILLIC] ?: DefaultUIFont
        }

        button.container.onClick {
            sceneContainer.changeTo<LobbyScene>()
        }

        return button
    }
}
